import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, ArrowRight, Sparkles, Loader2, Mail, Lock, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";

function formatApiErrorDetail(detail) {
    if (detail == null) return "Algo salió mal. Inténtalo de nuevo.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const from = location.state?.from?.pathname || "/app";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!email || !password) {
            setError("Introduce tu email y contraseña.");
            return;
        }
        setSubmitting(true);
        try {
            await login(email.trim().toLowerCase(), password);
            navigate(from, { replace: true });
        } catch (err) {
            setError(
                formatApiErrorDetail(err.response?.data?.detail) ||
                    err.message ||
                    "Error de acceso"
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="min-h-screen bg-[#f6f8ff] flex flex-col relative overflow-hidden"
            data-testid="login-page"
        >
            <div className="absolute inset-0 bg-grid-brand opacity-50 pointer-events-none" />
            <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-brand-600/25 to-cyan-400/25 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-cyan-400/20 to-brand-600/20 blur-3xl pointer-events-none" />

            <header className="relative z-10">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-600 to-cyan-400 text-white flex items-center justify-center shadow-pop">
                            <ShieldCheck size={17} strokeWidth={2.4} />
                        </div>
                        <span className="font-display text-xl font-extrabold tracking-tight">
                            LicitaIA
                        </span>
                    </Link>
                    <Link
                        to="/"
                        className="text-sm font-semibold text-ink-500 hover:text-brand-600 transition-colors"
                    >
                        ← Volver al inicio
                    </Link>
                </div>
            </header>

            <main className="flex-1 flex items-center justify-center p-6 relative z-10">
                <div className="w-full max-w-md glass shadow-pop-lg rounded-3xl p-8 md:p-10 border border-white">
                    <div className="inline-flex items-center gap-1.5 bg-brand-50 text-brand-700 px-3 py-1 rounded-full mb-5">
                        <Sparkles size={12} />
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em]">
                            Acceso privado
                        </span>
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl tracking-tight font-black mb-3 leading-[1.05]">
                        Entra a{" "}
                        <span className="bg-gradient-to-r from-brand-600 to-cyan-500 bg-clip-text text-transparent">
                            LicitaIA
                        </span>
                    </h1>
                    <p className="text-base text-ink-500 mb-8 leading-relaxed font-medium">
                        Accede con tu usuario y contraseña. Las cuentas las gestiona el administrador.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                        <div>
                            <label className="text-xs font-bold text-ink-700 uppercase tracking-wider mb-1.5 block">
                                Email
                            </label>
                            <div className="relative">
                                <Mail
                                    size={16}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
                                />
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="email"
                                    required
                                    placeholder="tu@empresa.com"
                                    className="pl-10 h-12 rounded-xl bg-white border-ink-200 focus-visible:ring-brand-500"
                                    data-testid="login-email-input"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-bold text-ink-700 uppercase tracking-wider mb-1.5 block">
                                Contraseña
                            </label>
                            <div className="relative">
                                <Lock
                                    size={16}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
                                />
                                <Input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    required
                                    placeholder="••••••••"
                                    className="pl-10 h-12 rounded-xl bg-white border-ink-200 focus-visible:ring-brand-500"
                                    data-testid="login-password-input"
                                />
                            </div>
                        </div>

                        {error && (
                            <div
                                className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm font-medium px-3 py-2.5 rounded-xl"
                                data-testid="login-error"
                            >
                                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                                <span>{error}</span>
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full h-14 rounded-2xl bg-brand-600 hover:bg-brand-700 text-white text-base font-bold shadow-pop hover:scale-[1.02] hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:translate-y-0"
                            data-testid="login-submit-btn"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={16} className="mr-2 animate-spin" />
                                    Entrando…
                                </>
                            ) : (
                                <>
                                    Entrar
                                    <ArrowRight size={16} className="ml-auto" />
                                </>
                            )}
                        </Button>
                    </form>

                    <div className="mt-6 text-xs text-ink-400 leading-relaxed font-medium">
                        ¿No tienes cuenta? Solicita una demo desde{" "}
                        <Link to="/" className="text-brand-600 font-bold hover:underline">
                            la página principal
                        </Link>{" "}
                        y el administrador te creará el acceso.
                    </div>
                </div>
            </main>
        </div>
    );
}
